/**
 * Save migrations.
 *
 * Each migration takes a state written under version N and returns one valid
 * under N+1. Migrations are deliberately defensive: an old save may be missing
 * whole subtrees, so every step fills in defaults rather than assuming shape.
 */

import type { PlayerState } from './types';
import { HABIT_KEYS, SKILL_KEYS, TRAIT_KEYS } from './types';
import { SAVE_VERSION } from './newgame';
import { CONTENT_VERSION } from './content';
import { generateBoard } from './board';
import { Rng } from './rng';

export type MigrationResult =
  | { ok: true; state: PlayerState; migrated: boolean }
  | { ok: false; error: string };

type Loose = Record<string, unknown>;

const MIGRATIONS: Record<number, (state: Loose) => Loose> = {
  // 1 → 2: introduced the inertia system and the scheduled-effect queue.
  1: (state) => {
    state['inertia'] ??= { pressure: 20, intentions: [], actedCount: 0, deferredCount: 0, decayedCount: 0 };
    state['scheduled'] ??= [];
    state['pendingEvents'] ??= [];
    return state;
  },
  // 2 → 3: market regimes, burnout stages, and the ending report.
  2: (state) => {
    state['market'] ??= { regime: 'normal', yearsInRegime: 0 };
    const burnout = state['burnout'] as Loose | undefined;
    if (!burnout || typeof burnout['stage'] !== 'string') {
      state['burnout'] = { stage: 'healthy', load: 8, yearsInStage: 0, episodes: 0, lastEpisodeAge: null };
    }
    state['ending'] ??= null;
    state['conditions'] ??= [];
    state['achievements'] ??= [];
    state['regrets'] ??= [];
    return state;
  },
};

/** Fill in anything a hand-edited or partially written save might be missing. */
function repair(loose: Loose): PlayerState {
  const state = loose as unknown as PlayerState;

  state.gameVersion ??= '0.0.0';
  state.contentVersion = CONTENT_VERSION;
  state.flags ??= {};
  state.counters ??= {};
  state.log ??= [];
  state.decisions ??= [];
  state.regrets ??= [];
  state.achievements ??= [];
  state.scheduled ??= [];
  state.conditions ??= [];
  state.aspirations ??= [];
  state.people ??= [];
  state.pendingEvents ??= [];
  state.visitedNodes ??= [];
  state.eventMemory ??= { lastSeenAge: {}, counts: {} };
  state.eventMemory.lastSeenAge ??= {};
  state.eventMemory.counts ??= {};
  state.market ??= { regime: 'normal', yearsInRegime: 0 };
  state.ending ??= null;
  state.frailty ??= 0;
  state.startAge ??= 18;

  for (const k of SKILL_KEYS) state.skills[k] ??= 0;
  for (const k of TRAIT_KEYS) state.traits[k] ??= 50;
  for (const k of HABIT_KEYS) state.habits[k] ??= 35;

  state.finance.debts ??= [];
  state.finance.lifetimeEarnings ??= 0;
  state.finance.lifetimeSpending ??= 0;
  state.finance.peakNetWorth ??= 0;
  state.finance.bankruptcies ??= 0;
  state.finance.emergencyMonthsTarget ??= 6;
  state.finance.property ??= { owned: false, value: 0, mortgageDebtId: null, purchaseAge: 0 };

  // A board can always be regenerated from the seed; a missing one is not fatal.
  if (!state.board || !state.board.nodes || !state.board.nodes[state.nodeId ?? '']) {
    state.board = generateBoard(new Rng(state.rng?.seed ?? 'RECOVERED').keyed('board'), 'board.default', state.startAge);
    state.nodeId = state.board.startId;
    state.visitedNodes = [state.board.startId];
  }

  state.saveVersion = SAVE_VERSION;
  return state;
}

export function migrate(input: PlayerState, fromVersion: number): MigrationResult {
  let loose = input as unknown as Loose;
  let migrated = false;
  let version = fromVersion;

  try {
    while (version < SAVE_VERSION) {
      const step = MIGRATIONS[version];
      if (!step) {
        // No explicit step: rely on `repair` to bring the shape up to date.
        version += 1;
        migrated = true;
        continue;
      }
      loose = step(loose);
      version += 1;
      migrated = true;
    }
    const state = repair(loose);
    if (typeof state.age !== 'number' || !state.identity || !state.rng?.seed) {
      return { ok: false, error: 'Save is missing essential fields.' };
    }
    return { ok: true, state, migrated };
  } catch (error) {
    return { ok: false, error: `Migration failed: ${String(error)}` };
  }
}
